import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchCases } from '../api/govopsOperationsService';
import { formatGovOpsError } from '../api/govopsErrors';
import { formatStatus, formatTimestamp } from '../constants/workflowLabels';
import PageShell from '../components/PageShell';

const STATUS_CLASS = {
  intake: 'rhs-badge--intake',
  under_review: 'rhs-badge--review',
  notice_sent: 'rhs-badge--notice',
  violation_open: 'rhs-badge--violation',
  payment_pending: 'rhs-badge--payment',
  resolved: 'rhs-badge--resolved',
  closed: 'rhs-badge--closed',
  appealed: 'rhs-badge--appeal',
};

const AdminCasesPage = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setCases(await fetchCases());
    } catch (e) {
      setError(formatGovOpsError(e));
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = cases.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [c.id, c.title, c.city, c.status].filter(Boolean).join(' ').toLowerCase().includes(q);
  });

  return (
    <PageShell
      title="Cases"
      subtitle="GovOps cases for your organization — open a case for audit timeline and exports."
      className="rhs-admin-cases"
    >
      <nav className="rhs-quick-nav" aria-label="Admin navigation">
        <Link to="/admin" className="rhs-quick-nav-item">
          ← Operations dashboard
        </Link>
        <Link to="/admin/table" className="rhs-quick-nav-item">
          Manage permits
        </Link>
      </nav>

      {error && (
        <div className="rhs-error-panel" role="alert">
          <strong>Error</strong>
          <p className="rhs-muted">{error}</p>
        </div>
      )}

      <div className="rhs-toolbar">
        <label className="rhs-search" htmlFor="admin-cases-search">
          <span className="rhs-sr-only">Search cases</span>
          <input
            id="admin-cases-search"
            type="search"
            placeholder="Search by ID, title, city, status…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <button type="button" className="rhs-btn rhs-btn--secondary" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {loading && (
        <div className="rhs-loading" role="status">
          <span className="rhs-spinner" aria-hidden="true" />
          Loading cases…
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <p className="rhs-muted">No cases match your search.</p>
      )}

      {!loading && filtered.length > 0 && (
        <div className="rhs-table-wrap">
          <table className="rhs-table">
            <thead>
              <tr>
                <th scope="col">Case</th>
                <th scope="col">Title</th>
                <th scope="col">City</th>
                <th scope="col">Status</th>
                <th scope="col">Updated</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const badge = STATUS_CLASS[c.status] || 'rhs-badge--default';
                return (
                  <tr key={c.id}>
                    <td className="rhs-permit-id" title={c.id}>
                      {c.id.slice(0, 12)}…
                    </td>
                    <td>{c.title || '—'}</td>
                    <td>{c.city || '—'}</td>
                    <td>
                      <span className={`rhs-badge ${badge}`}>{formatStatus(c.status)}</span>
                    </td>
                    <td>{formatTimestamp(c.updatedAt || c.createdAt)}</td>
                    <td className="rhs-cell-actions">
                      <Link to={`/cases/${c.id}`} className="rhs-table-link">
                        Timeline
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
};

export default AdminCasesPage;
